import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Single generic report modal — replaces GlobalReportModal.js, UserReportModal.js,
// and AuctionReportModal.js. Report metadata (label/description/params) and
// data come entirely from the backend registry (backend/services/reportRegistry.js);
// this component has no per-report-type branching. The preview table and CSV
// download are both driven by `csvRows` (a flat array of plain objects every
// report builder returns alongside the richer `data` payload) — adding a new
// report on the backend needs no frontend change at all.
const ReportsModal = ({ onClose, events = [], initialReportKey = null, initialParams = {} }) => {
    const [reports, setReports] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [selectedKey, setSelectedKey] = useState(initialReportKey);
    const [params, setParams] = useState(initialParams);
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState('');
    const [farmers, setFarmers] = useState([]);

    useEffect(() => {
        api.get('/admin/reports')
            .then(res => setReports(res.data))
            .catch(() => setError('Failed to load available reports.'))
            .finally(() => setLoadingList(false));

        // Backs the "Farmer" param picker (bid_detail_export) — /admin/user-stats
        // returns one row per user+SBI, so a farmer with multiple SBIs/parcels
        // appears more than once; dedupe down to one dropdown entry per farmer.
        api.get('/admin/user-stats')
            .then(res => {
                const seen = new Map();
                res.data.filter(r => r.role === 'user').forEach(r => {
                    if (!seen.has(r.userId)) seen.set(r.userId, { userId: r.userId, name: r.userName, email: r.email });
                });
                setFarmers([...seen.values()].sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(() => setFarmers([]));
    }, []);

    const selectedReport = reports.find(r => r.key === selectedKey);

    const runReport = useCallback(async (key, runParams) => {
        setRunning(true);
        setError('');
        setResult(null);
        try {
            const query = new URLSearchParams();
            Object.entries(runParams || {}).forEach(([k, v]) => { if (v) query.set(k, v); });
            const qs = query.toString();
            const res = await api.get(`/admin/reports/${key}${qs ? `?${qs}` : ''}`);
            setResult(res.data);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to run report.');
        } finally {
            setRunning(false);
        }
    }, []);

    // Auto-run as soon as a report is selected and every required param is
    // already satisfied (e.g. opened from a specific auction/farmer context);
    // otherwise wait for the admin to fill params and click "Run Report".
    useEffect(() => {
        if (!selectedKey) return;
        const report = reports.find(r => r.key === selectedKey);
        if (!report) return;
        const stillMissing = (report.params || []).some(p => p.required && !params[p.key]);
        if (!stillMissing) runReport(selectedKey, params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey, reports]);

    const handleSelectReport = (key) => {
        setSelectedKey(key);
        setParams(key === initialReportKey ? initialParams : {});
        setResult(null);
    };

    const handleParamChange = (key, value) => setParams(prev => ({ ...prev, [key]: value }));
    const handleRunClick = () => runReport(selectedKey, params);

    // Display-only formatting — csvRows/the preview table are what a human
    // looks at, so numbers get thousands separators and a magnitude-scaled
    // decimal precision, and timestamps get shortened to a plain date (raw
    // values in `result.data` are left untouched, since that payload is
    // meant for programmatic consumption, e.g. a future pre-contract email
    // service, not for on-screen display).
    const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    const formatCellValue = (value) => {
        if (value === null || value === undefined || value === '') return null;
        if (typeof value === 'number' && !Number.isNaN(value)) {
            const abs = Math.abs(value);
            const decimals = abs >= 1000 ? 0 : abs >= 100 ? 1 : 2;
            return value.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        }
        if (typeof value === 'string' && ISO_DATETIME_RE.test(value)) {
            const parsed = new Date(value);
            if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString('en-GB');
        }
        return String(value);
    };

    // "totalBidValue" -> "Total Bid Value" — used for the summary box below,
    // so any report can add a data.summary object and get a labeled box for
    // free without the frontend needing to know its field names in advance.
    const humanizeKey = (key) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase());

    const handleDownloadCSV = () => {
        const rows = result?.csvRows;
        if (!rows || rows.length === 0) return;
        const headers = Object.keys(rows[0]);
        let csvContent = 'data:text/csv;charset=utf-8,';
        csvContent += headers.join(',') + '\n';
        csvContent += rows.map(row => headers.map(h => `"${(formatCellValue(row[h]) ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
        const link = document.createElement('a');
        link.setAttribute('href', encodeURI(csvContent));
        link.setAttribute('download', `${selectedKey || 'report'}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const missingRequiredParams = (selectedReport?.params || []).filter(p => p.required && !params[p.key]);
    const csvRows = result?.csvRows || [];
    const csvHeaders = csvRows.length > 0 ? Object.keys(csvRows[0]) : [];

    // The RPA code and the farmer's own bespoke field name are separate CSV
    // columns (so a spreadsheet can filter/sort each independently), but on
    // screen they read better merged into one cell — code on top, bespoke
    // name in green underneath. See resolveParcelIdentity in reportBuilders.js.
    const PARCEL_REF_KEY = 'Parcel Ref';
    const BESPOKE_NAME_KEY = 'Bespoke Name';
    const hasParcelPair = csvHeaders.includes(PARCEL_REF_KEY) && csvHeaders.includes(BESPOKE_NAME_KEY);
    const displayHeaders = hasParcelPair ? csvHeaders.filter(h => h !== BESPOKE_NAME_KEY) : csvHeaders;

    const summary = (result?.data && typeof result.data === 'object' && !Array.isArray(result.data)) ? result.data.summary : null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={{ margin: 0, color: '#0F172A' }}>{selectedReport ? selectedReport.label : 'Reports'}</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {selectedKey && <button onClick={() => handleSelectReport(null)} style={styles.btnAction}>← All Reports</button>}
                        <button onClick={onClose} style={styles.btnClose}>✕</button>
                    </div>
                </div>

                <div style={styles.body}>
                    {loadingList && <p>Loading available reports...</p>}
                    {error && <p style={{ color: '#DC2626' }}>{error}</p>}

                    {!selectedKey && !loadingList && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                            {reports.map(r => (
                                <div key={r.key} style={styles.card}>
                                    <h4 style={{ marginTop: 0 }}>{r.label}</h4>
                                    <p style={{ fontSize: '0.85rem', color: '#475569' }}>{r.description}</p>
                                    <button onClick={() => handleSelectReport(r.key)} style={styles.btnAction}>Open</button>
                                </div>
                            ))}
                            {reports.length === 0 && <p>No reports available.</p>}
                        </div>
                    )}

                    {selectedKey && selectedReport && (
                        <>
                            {selectedReport.params.length > 0 && (
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '15px', flexWrap: 'wrap' }}>
                                    {selectedReport.params.map(p => (
                                        <div key={p.key}>
                                            <label style={{ display: 'block', fontSize: '0.8rem', color: '#475569', marginBottom: '4px' }}>{p.label}</label>
                                            {p.type === 'event' ? (
                                                <select value={params.eventId || ''} onChange={e => handleParamChange('eventId', e.target.value)} style={styles.input}>
                                                    <option value="">{p.required ? '-- Select an event --' : '-- All events --'}</option>
                                                    {events.map(ev => <option key={ev._id} value={ev._id}>{ev.name}</option>)}
                                                </select>
                                            ) : p.type === 'user' ? (
                                                <select value={params.userId || ''} onChange={e => handleParamChange('userId', e.target.value)} style={styles.input}>
                                                    <option value="">{p.required ? '-- Select a farmer --' : '-- All farmers --'}</option>
                                                    {farmers.map(f => <option key={f.userId} value={f.userId}>{f.name} ({f.email})</option>)}
                                                </select>
                                            ) : (
                                                <span style={styles.readOnlyValue}>{params[p.key] || '(not set)'}</span>
                                            )}
                                        </div>
                                    ))}
                                    <button onClick={handleRunClick} disabled={missingRequiredParams.length > 0 || running} style={styles.btnPrimary}>
                                        {running ? 'Running...' : 'Filter Report'}
                                    </button>
                                </div>
                            )}

                            {running && <p>Generating report...</p>}

                            {result && (
                                <>
                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                        <button onClick={handleDownloadCSV} style={styles.btnAction} disabled={csvRows.length === 0}>💾 Download CSV/Data</button>
                                    </div>

                                    {csvRows.length > 0 ? (
                                        <div style={styles.scrollArea}>
                                            <table style={styles.table}>
                                                <thead>
                                                    <tr>{displayHeaders.map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
                                                </thead>
                                                <tbody>
                                                    {csvRows.map((row, i) => (
                                                        <tr key={i}>
                                                            {displayHeaders.map(h => (
                                                                <td key={h} style={styles.td}>
                                                                    {formatCellValue(row[h]) ?? '-'}
                                                                    {h === PARCEL_REF_KEY && hasParcelPair && row[BESPOKE_NAME_KEY] && (
                                                                        <div style={styles.bespokeName}>{row[BESPOKE_NAME_KEY]}</div>
                                                                    )}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p style={{ fontStyle: 'italic', color: '#475569' }}>No rows to display.</p>
                                    )}

                                    {summary && (
                                        <div style={styles.summaryBox}>
                                            {Object.entries(summary).map(([key, value]) => (
                                                <div key={key} style={styles.summaryItem}>
                                                    <div style={styles.summaryLabel}>{humanizeKey(key)}</div>
                                                    <div style={styles.summaryValue}>{formatCellValue(value) ?? '-'}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
    modal: { backgroundColor: '#FFFFFF', width: '1400px', maxWidth: '98%', height: '90%', borderRadius: '8px', padding: '0', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' },
    header: { padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC' },
    body: { flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC' },
    scrollArea: { overflow: 'auto', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#FFFFFF' },
    card: { padding: '20px', border: '1px solid #ddd', borderRadius: '8px', textAlign: 'center', backgroundColor: '#FFFFFF' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
    th: { textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid #eee', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', whiteSpace: 'normal', overflowWrap: 'break-word', minWidth: '95px', maxWidth: '170px', lineHeight: '1.3' },
    td: { padding: '8px 6px', borderBottom: '1px solid #f9f9f9', color: '#0F172A', whiteSpace: 'nowrap' },
    input: { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' },
    readOnlyValue: { display: 'inline-block', padding: '8px', backgroundColor: '#F1F5F9', borderRadius: '4px', color: '#475569', fontSize: '0.85rem' },
    bespokeName: { color: '#0D9488', fontSize: '0.75rem', fontWeight: 'bold' },
    summaryBox: { display: 'flex', gap: '24px', flexWrap: 'wrap', padding: '16px 20px', marginTop: '15px', backgroundColor: '#FFFFFF', border: '2px solid #0D9488', borderRadius: '8px' },
    summaryItem: { display: 'flex', flexDirection: 'column' },
    summaryLabel: { fontSize: '0.7rem', textTransform: 'uppercase', color: '#475569', marginBottom: '2px' },
    summaryValue: { fontSize: '1.1rem', fontWeight: 'bold', color: '#0F172A' },
    btnAction: { padding: '8px 12px', cursor: 'pointer', border: '1px solid #0D9488', backgroundColor: '#FFFFFF', color: '#0D9488', borderRadius: '4px', fontWeight: 'bold' },
    btnPrimary: { padding: '8px 16px', cursor: 'pointer', border: 'none', backgroundColor: '#0D9488', color: '#FFFFFF', borderRadius: '4px', fontWeight: 'bold' },
    btnClose: { padding: '8px 12px', cursor: 'pointer', border: 'none', backgroundColor: '#0D9488', color: '#F59E0B', borderRadius: '4px', fontWeight: 'bold' },
};

export default ReportsModal;
